// @ts-nocheck
import React, { Component } from 'react';
import _ from 'lodash';
import { inject, observer } from "../../../../stores/mobx-compat";

import Card from '../../../ui/Card';
import Button from '../../../ui/Button';
import IconButton from '../../../ui/IconButton';
import Modal, { ModalActions } from '../../../ui/Modal';
import Alert from '../../../ui/Alert';
import FormGroup from '../../../ui/FormGroup';
import Input from '../../../ui/Input';
import Checkbox from '../../../ui/Checkbox';
import Empty from '../../../ui/Empty';
import {
  PlusIcon, EditIcon, XIcon, ExternalLinkIcon, LinkIcon,
} from '../../../ui/icons';
import { modalTypes } from '../../../../enums';
import { validateUrl } from '../../../../helper';

const WEBHOOK_DOCS_LINK = 'https://docs.movology.com/docs/setting-up-webhooks/';
const FIELDS = [
  { key: 'created_ts', label: 'Captured timestamp' },
  { key: 'email', label: 'Email address' },
  { key: 'email_count', label: 'Recover emails sent' },
  { key: 'form_data', label: 'Form captured' },
  { key: 'url', label: 'Capture origin URL' },
];

class Webhooks extends Component {
  state = {
    modalOpen: false,
    modalType: modalTypes.add,
    editKey: null,
    deleteKey: null,
    hookObj: this.props.store.clientHooks.hookObjTemplate,
  }

  componentDidMount() {
    const { clientHooks } = this.props.store;
    if (Object.keys(clientHooks.hooks).length === 0) clientHooks.getAllHooks();
  }

  closeModal = () => this.setState({
    modalOpen: false,
    hookObj: this.props.store.clientHooks.hookObjTemplate,
  });

  validateForm = () => {
    if (!validateUrl(this.state.hookObj.url)) {
      this.props.store.failure('Invalid URL entered.');
      return false;
    }
    if (this.state.hookObj.name.trim() === '') {
      this.props.store.failure('Name field empty.');
      return false;
    }
    return true;
  }

  setField = (name, value) => this.setState({ hookObj: { ...this.state.hookObj, [name]: value } });

  toggleData = (name) => this.setState({
    hookObj: {
      ...this.state.hookObj,
      dataToSend: {
        capture: {
          ...this.state.hookObj.dataToSend.capture,
          [name]: !this.state.hookObj.dataToSend.capture[name],
        },
      },
    },
  });

  hookForm = () => (
    <>
      <FormGroup label="Webhook name">
        <Input
          value={this.state.hookObj.name}
          placeholder="Webhook #1"
          onChange={(e) => this.setField('name', e.target.value)}
        />
      </FormGroup>
      <FormGroup label="Webhook URL">
        <Input
          value={this.state.hookObj.url}
          placeholder="https://mysite.com"
          onChange={(e) => this.setField('url', e.target.value)}
        />
      </FormGroup>
      <FormGroup label="Data to send">
        <div className="flex flex-col gap-2 mt-1">
          {FIELDS.map((f) => (
            <Checkbox
              key={f.key}
              label={f.label}
              checked={!!this.state.hookObj.dataToSend.capture[f.key]}
              onChange={() => this.toggleData(f.key)}
            />
          ))}
        </div>
      </FormGroup>
    </>
  );

  onConfirm = () => {
    const { addHook, updateHook, deleteHook } = this.props.store.clientHooks;
    if (this.state.modalType === modalTypes.add) {
      if (this.validateForm()) {
        addHook(this.state.hookObj);
        this.closeModal();
      }
    } else if (this.state.modalType === modalTypes.edit) {
      if (this.validateForm()) {
        updateHook(this.state.editKey, this.state.hookObj);
        this.closeModal();
      }
    } else if (this.state.modalType === modalTypes.remove) {
      deleteHook(this.state.deleteKey);
      this.closeModal();
    }
  }

  modalTitle = () => {
    const { hooks } = this.props.store.clientHooks;
    if (this.state.modalType === modalTypes.add) return 'Add a webhook';
    if (this.state.modalType === modalTypes.edit) {
      return `Update ${hooks[this.state.editKey] ? hooks[this.state.editKey].name : 'webhook'}`;
    }
    return 'Delete this webhook?';
  }

  render() {
    const { hooks } = this.props.store.clientHooks;
    const list = Object.entries(hooks);
    const limitReached = list.length >= 5;
    const removalText = hooks[this.state.deleteKey] ? hooks[this.state.deleteKey].name : '';

    return (
      <>
        <Alert tone="info" title="New to webhooks?">
          Read the docs at{' '}
          <a href={WEBHOOK_DOCS_LINK} target="_blank" rel="noopener noreferrer" className="text-mov-accent-ink underline">
            docs.movology.com
          </a> to learn how deliveries work.
        </Alert>

        <div className="flex items-center justify-between mt-5 mb-4">
          <h3 className="m-0 text-[14.5px] font-semibold">Your webhooks ({list.length}/5)</h3>
          <div className="flex gap-2">
            <Button icon={ExternalLinkIcon} onClick={() => window.open(WEBHOOK_DOCS_LINK, '_blank')}>
              Read the docs
            </Button>
            <Button
              variant="primary"
              icon={PlusIcon}
              disabled={limitReached}
              onClick={() => this.setState({
                modalOpen: true,
                modalType: modalTypes.add,
                hookObj: this.props.store.clientHooks.hookObjTemplate,
              })}
            >
              Add webhook
            </Button>
          </div>
        </div>

        {list.length === 0 ? (
          <Empty
            icon={LinkIcon}
            title="No webhooks yet"
            sub="Webhooks deliver capture events to your endpoints in real time."
            actions={(
              <Button
                variant="primary"
                icon={PlusIcon}
                onClick={() => this.setState({
                  modalOpen: true,
                  modalType: modalTypes.add,
                  hookObj: this.props.store.clientHooks.hookObjTemplate,
                })}
              >
                Add your first webhook
              </Button>
            )}
          />
        ) : (
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}
          >
            {list.map(([key, hook]) => (
              <Card key={key} padded>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h4 className="m-0 text-[14px] font-semibold truncate">{hook.name}</h4>
                  <div className="flex items-center gap-1">
                    <IconButton
                      variant="ghost"
                      icon={EditIcon}
                      ariaLabel="Edit"
                      onClick={() => this.setState({
                        modalOpen: true,
                        modalType: modalTypes.edit,
                        editKey: key,
                        hookObj: _.cloneDeep(hook),
                      })}
                    />
                    <IconButton
                      variant="ghost"
                      icon={XIcon}
                      ariaLabel="Delete"
                      onClick={() => this.setState({
                        modalOpen: true,
                        modalType: modalTypes.remove,
                        deleteKey: key,
                      })}
                    />
                  </div>
                </div>
                <dl className="m-0 grid gap-2.5 text-[12.5px]">
                  <Row label="Request URL">
                    <span className="mov-mono break-all" style={{ wordBreak: 'break-all' }}>{hook.url}</span>
                  </Row>
                  <Row label="Secret key">
                    <span className="mov-mono select-all" style={{ wordBreak: 'break-all' }}>
                      {'•'.repeat(Math.min((hook.secretKey || '').length, 25))}
                    </span>
                  </Row>
                </dl>
              </Card>
            ))}
          </div>
        )}

        <Modal
          open={this.state.modalOpen}
          onClose={this.closeModal}
          title={this.modalTitle()}
          size={this.state.modalType === modalTypes.remove ? 'sm' : 'md'}
          actions={(
            <ModalActions
              onCancel={this.closeModal}
              onConfirm={this.onConfirm}
              confirmLabel={
                this.state.modalType === modalTypes.add ? 'Add'
                  : this.state.modalType === modalTypes.edit ? 'Update' : 'Delete'
              }
              confirmVariant={this.state.modalType === modalTypes.remove ? 'danger' : 'primary'}
            />
          )}
        >
          {this.state.modalType === modalTypes.remove
            ? <p className="m-0 text-[13px] text-mov-ink-soft">{removalText} will be deleted permanently.</p>
            : this.hookForm()}
        </Modal>
      </>
    );
  }
}

const Row = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3" style={{ borderBottom: '1px dashed var(--mov-line)', paddingBottom: 8 }}>
    <dt className="text-mov-ink-mute mov-mono text-[10.5px] uppercase tracking-wider shrink-0">{label}</dt>
    <dd className="m-0 min-w-0">{children}</dd>
  </div>
);

export default inject('store')(observer(Webhooks));
